import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Skeleton } from '@/components/ui/skeleton';
import { useSession } from '@/services/providers/session-provider';
import { Globe, Settings, User, LogOut } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { AvailableI18Languages, TI18Language } from '@/services/i18n/i18n.tsx';
import { Link } from 'react-router';

export const MyProfileAvatar = () => {
  const { firstName, lastName, logOut, isLoading } = useSession();
  const { t, i18n } = useTranslation();

  if (isLoading) {
    return <Skeleton className="size-10 rounded-full" />;
  }

  const handleLanguageChange = (code: TI18Language['code']) => {
    i18n.changeLanguage(code);
    localStorage.setItem('preferredLanguage', code);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Avatar className="cursor-pointer transition-opacity hover:opacity-80">
          <AvatarImage src="" />
          <AvatarFallback className="bg-primary/10 text-primary font-medium">
            {firstName?.[0]}
            {lastName?.[0]}
          </AvatarFallback>
        </Avatar>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="w-56">
        <div className="flex items-center gap-2 p-2">
          <Avatar className="size-8">
            <AvatarImage src="" />
            <AvatarFallback className="bg-primary/10 text-primary text-sm">
              {firstName?.[0]}
              {lastName?.[0]}
            </AvatarFallback>
          </Avatar>
          <div className="flex flex-col">
            <span className="text-sm font-medium">
              {firstName} {lastName}
            </span>
          </div>
        </div>

        <DropdownMenuSeparator />

        <Link to="/my-profile">
          <DropdownMenuItem className="gap-2">
            <User className="size-4" />
            <span>{t('profile.menu.myProfile')}</span>
          </DropdownMenuItem>
        </Link>

        <DropdownMenuItem disabled className="gap-2">
          <Settings className="size-4" />
          <span>{t('profile.menu.settings')}</span>
        </DropdownMenuItem>

        <DropdownMenuSub>
          <DropdownMenuSubTrigger className="gap-2">
            <Globe className="size-4" />
            <span>{t('profile.menu.language')}</span>
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent className="w-40">
            {AvailableI18Languages.map(({ code, Icon }) => (
              <DropdownMenuItem
                key={code}
                className="cursor-pointer gap-2"
                onClick={() => handleLanguageChange(code)}
              >
                {Icon ? <Icon className="size-4" /> : <span className="size-4"></span>}
                <span>{t(`languages.${code}`)}</span>
                {i18n.language === code && <span className="text-primary ml-auto">✓</span>}
              </DropdownMenuItem>
            ))}
          </DropdownMenuSubContent>
        </DropdownMenuSub>

        <DropdownMenuSeparator />

        <DropdownMenuItem
          onClick={() => logOut()}
          className="gap-2 text-red-600 focus:text-red-600"
        >
          <LogOut className="size-4" />
          <span>{t('profile.menu.logout')}</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default MyProfileAvatar;
